import type * as React from "react";

export interface TabItem {
  id: string;
  label: React.ReactNode;
  /** Optional count pill. */
  count?: number;
  /** Optional leading icon node. */
  icon?: React.ReactNode;
}

/** Underline tab bar with optional count pills. */
export interface TabsProps {
  items: TabItem[];
  /** Active tab id (controlled). */
  value: string;
  onChange?: (id: string) => void;
  style?: React.CSSProperties;
}

export function Tabs({ items = [], value, onChange, style }: TabsProps) {
  return (
    <div
      role="tablist"
      style={{ display: "flex", gap: "var(--space-5)", borderBottom: "1px solid var(--border-subtle)", ...style }}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange && onChange(item.id)}
            style={
              {
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                padding: "0 0 11px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                position: "relative",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-body-sm)",
                fontWeight: active ? "var(--fw-bold)" : "var(--fw-semibold)",
                color: active ? "var(--text-strong)" : "var(--text-muted)",
                transition: "color var(--duration-fast) var(--ease-out)",
              } as React.CSSProperties
            }
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.color = "var(--text-body)";
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            {item.icon}
            {item.label}
            {item.count != null ? (
              <span
                style={
                  {
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-micro)",
                    fontWeight: "var(--fw-bold)",
                    color: active ? "var(--accent-soft-text)" : "var(--text-muted)",
                    background: active ? "var(--accent-soft)" : "var(--surface-sunken)",
                    borderRadius: "var(--radius-pill)",
                    padding: "1px 7px",
                    lineHeight: 1.5,
                  } as React.CSSProperties
                }
              >
                {item.count}
              </span>
            ) : null}
            <span
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "-1px",
                height: "2px",
                borderRadius: "2px",
                background: "var(--accent)",
                opacity: active ? 1 : 0,
                transition: "opacity var(--duration-fast) var(--ease-out)",
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
